import { useState } from 'react';

const steps = [
    { id: 'Step 1', name: 'Business Info', href: '#', status: 'complete' },
    { id: 'Step 2', name: 'Categories', href: '#', status: 'current' },
    { id: 'Step 3', name: 'Media Info', href: '#', status: 'upcoming' },
    { id: 'Step 4', name: 'Working Hours', href: '#', status: 'upcoming' },
    { id: 'Step 5', name: 'Review & Submit', href: '#', status: 'upcoming' },
];

const initialFormState = {
    businessInformation: {
        businessName: '',
        phone: '',
        address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            country: '',
            zipCode: '',
        },
        ownerName: '',
        businessEmail: '',
    },
    categoriesInformation: {
        businessCategory: '',
        otherCategory: '',
        subcategory: '',
        socialMediaLinks: [],
        websiteURL: '',
        logoURL: '',
        videoName: '',
        relatedSearches: [],
        description: '',
        keyword: '',
        specialInstructions: '',
    },
    mediaInformation: {
        uploadedImages: [],
    },
    workingHours: {
        monday: { openHours: '', openMinutes: '', closeHours: '', closeMinutes: '' },
        tuesday: { openHours: '', openMinutes: '', closeHours: '', closeMinutes: '' },
        wednesday: { openHours: '', openMinutes: '', closeHours: '', closeMinutes: '' },
        thursday: { openHours: '', openMinutes: '', closeHours: '', closeMinutes: '' },
        friday: { openHours: '', openMinutes: '', closeHours: '', closeMinutes: '' },
        saturday: { openHours: '', openMinutes: '', closeHours: '', closeMinutes: '' },
        sunday: { openHours: '', openMinutes: '', closeHours: '', closeMinutes: '' },
    },
};

function Stepper({ currentStep }: { currentStep: number }) {
    return (
        <nav aria-label="Progress">
            <ol role="list" className="space-y-4 md:flex md:space-x-8 md:space-y-0">
                {steps.map((step, index) => (
                    <li key={step.name} className="md:flex-1">
                        <a
                            href={step.href}
                            className={`group flex flex-col border-l-4 ${index < currentStep
                                ? 'border-green-600'
                                : index === currentStep
                                    ? 'border-green-600'
                                    : 'border-gray-200'
                                } py-2 pl-4 hover:border-green-800 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4`}
                        >
                            <span
                                className={`text-sm font-medium ${index < currentStep
                                    ? 'text-green-600 group-hover:text-green-800'
                                    : index === currentStep
                                        ? 'text-green-600'
                                        : 'text-gray-500 group-hover:text-gray-700'
                                    }`}
                            >
                                {step.id}
                            </span>
                            <span className="text-sm font-medium">{step.name}</span>
                        </a>
                    </li>
                ))}
            </ol>
        </nav>
    );
}

const CreateBusiness = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState(initialFormState);

    const handleNext = () => {
        if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
    };

    const handlePrevious = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1);
    };

    const handleInputChange = (section, field, value) => {
        setFormData((prevData) => ({
            ...prevData,
            [section]: {
                ...prevData[section],
                [field]: value,
            },
        }));
    };

    const handleSubmit = () => {
        alert('Business Information Submitted!');
    };

    return (
        <div className="p-6">
            <Stepper currentStep={currentStep} />
            <div className="mt-6">
                {currentStep === 0 && (
                    <div>
                        <h2 className="text-lg font-bold mb-6">Business Information</h2>

                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            {/* Business Name */}
                            <div>
                                <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
                                    Business Name
                                </label>
                                <input
                                    id="businessName"
                                    type="text"
                                    placeholder="Business Name"
                                    value={formData.businessInformation.businessName}
                                    onChange={(e) =>
                                        handleInputChange('businessInformation', 'businessName', e.target.value)
                                    }
                                    className="mt-1 py-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                />
                            </div>

                            {/* Phone Number */}
                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                    Phone Number
                                </label>
                                <input
                                    id="phone"
                                    type="text"
                                    placeholder="Phone Number"
                                    value={formData.businessInformation.phone}
                                    onChange={(e) =>
                                        handleInputChange('businessInformation', 'phone', e.target.value)
                                    }
                                    className="mt-1 py-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                />
                            </div>

                            {/* Owner's Name */}
                            <div>
                                <label htmlFor="ownerName" className="block text-sm font-medium text-gray-700">
                                    Owner's Name
                                </label>
                                <input
                                    id="ownerName"
                                    type="text"
                                    placeholder="Owner Name"
                                    value={formData.businessInformation.ownerName}
                                    onChange={(e) =>
                                        handleInputChange('businessInformation', 'ownerName', e.target.value)
                                    }
                                    className="mt-1 py-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                />
                            </div>

                            {/* Business Email */}
                            <div>
                                <label htmlFor="businessEmail" className="block text-sm font-medium text-gray-700">
                                    Business Email
                                </label>
                                <input
                                    id="businessEmail"
                                    type="email"
                                    placeholder="Business Email"
                                    value={formData.businessInformation.businessEmail}
                                    onChange={(e) =>
                                        handleInputChange('businessInformation', 'businessEmail', e.target.value)
                                    }
                                    className="mt-1 py-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                />
                            </div>

                            {/* Address Line 1 */}
                            <div>
                                <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700">
                                    Address Line 1
                                </label>
                                <input
                                    id="addressLine1"
                                    type="text"
                                    placeholder="Address Line 1"
                                    value={formData.businessInformation.address.line1}
                                    onChange={(e) =>
                                        handleInputChange('businessInformation', 'address.line1', e.target.value)
                                    }
                                    className="mt-1 py-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                />
                            </div>

                            {/* Address Line 2 */}
                            <div>
                                <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700">
                                    Address Line 2
                                </label>
                                <input
                                    id="addressLine2"
                                    type="text"
                                    placeholder="Address Line 2"
                                    value={formData.businessInformation.address.line2}
                                    onChange={(e) =>
                                        handleInputChange('businessInformation', 'address.line2', e.target.value)
                                    }
                                    className="mt-1 py-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                />
                            </div>

                            {/* City */}
                            <div>
                                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                                    City
                                </label>
                                <input
                                    id="city"
                                    type="text"
                                    placeholder="City"
                                    value={formData.businessInformation.address.city}
                                    onChange={(e) =>
                                        handleInputChange('businessInformation', 'address.city', e.target.value)
                                    }
                                    className="mt-1 py-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                />
                            </div>

                            {/* State */}
                            <div>
                                <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                                    State
                                </label>
                                <input
                                    id="state"
                                    type="text"
                                    placeholder="State"
                                    value={formData.businessInformation.address.state}
                                    onChange={(e) =>
                                        handleInputChange('businessInformation', 'address.state', e.target.value)
                                    }
                                    className="mt-1 py-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                />
                            </div>

                            {/* Country */}
                            <div>
                                <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                                    Country
                                </label>
                                <input
                                    id="country"
                                    type="text"
                                    placeholder="Country"
                                    value={formData.businessInformation.address.country}
                                    onChange={(e) =>
                                        handleInputChange('businessInformation', 'address.country', e.target.value)
                                    }
                                    className="mt-1 py-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                />
                            </div>

                            {/* Zip Code */}
                            <div>
                                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
                                    Zip Code
                                </label>
                                <input
                                    id="zipCode"
                                    type="text"
                                    placeholder="Zip Code"
                                    value={formData.businessInformation.address.zipCode}
                                    onChange={(e) =>
                                        handleInputChange('businessInformation', 'address.zipCode', e.target.value)
                                    }
                                    className="mt-1 py-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                />
                            </div>
                        </div>
                    </div>
                )}
                {currentStep === 1 && (
                    <div>
                        <h2 className="text-lg font-bold">Categories</h2>
                        <input
                            type="text"
                            placeholder="Business Category"
                            value={formData.categoriesInformation.businessCategory}
                            onChange={(e) =>
                                handleInputChange('categoriesInformation', 'businessCategory', e.target.value)
                            }
                            className="block w-full p-2 border border-gray-300 rounded mt-2"
                        />
                        {/* Add more fields as necessary */}
                    </div>
                )}

                {currentStep === 2 && (
                    <div>
                        <h2 className="text-lg font-bold">Media Information</h2>
                        <input
                            type="text"
                            placeholder="Logo URL"
                            value={formData.categoriesInformation.logoURL}
                            onChange={(e) =>
                                handleInputChange('categoriesInformation', 'logoURL', e.target.value)
                            }
                            className="block w-full p-2 border border-gray-300 rounded mt-2"
                        />
                        {/* Add more fields as necessary */}
                    </div>
                )}

                {currentStep === 3 && (
                    <div>
                        <h2 className="text-lg font-bold">Working Hours</h2>
                        <input
                            type="text"
                            placeholder="Monday Open Hours"
                            value={formData.workingHours.monday.openHours}
                            onChange={(e) =>
                                handleInputChange('workingHours', 'monday.openHours', e.target.value)
                            }
                            className="block w-full p-2 border border-gray-300 rounded mt-2"
                        />
                        {/* Add more fields as necessary */}
                    </div>
                )}

                {currentStep === 4 && (
                    <div>
                        <h2 className="text-lg font-bold">Review & Submit</h2>
                        <pre>{JSON.stringify(formData, null, 2)}</pre>
                    </div>
                )}
            </div>

            <div className="mt-6 flex justify-between">
                <button
                    onClick={handlePrevious}
                    disabled={currentStep === 0}
                    className="px-4 py-2 bg-gray-300 rounded"
                >
                    Previous
                </button>
                {currentStep < steps.length - 1 ? (
                    <button
                        onClick={handleNext}
                        className="px-4 py-2 bg-green-500 text-white rounded"
                    >
                        Next
                    </button>
                ) : (
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-green-500 text-white rounded"
                    >
                        Submit
                    </button>
                )}
            </div>
        </div>
    );
}


export default CreateBusiness;